import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CloudProvisionerTemplateForm, CloudProvisionerTemplateRequestIonos, CloudProvisionerTemplateRequestOvh } from "~/models/Commons ";

import { MESSAGES } from "~/resources/Message";
import {
  useAddCloudProvisionerTemplateIonosMutation,
  useAddCloudProvisionerTemplateOvhMutation,
} from "~/services/cloudProvisioner";
import { normalizeFillArray } from "~/Utils/Utils";

export const UseFormCloudProvisioner = (refetch: () => void) => {
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [saveCloudProvisionerTemplateOvh, { isLoading: isOvhLoading, isError }] =
    useAddCloudProvisionerTemplateOvhMutation();
  const [saveCloudProvisionerTemplateIonos] =
    useAddCloudProvisionerTemplateIonosMutation();

  const navigate = useNavigate();

  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm<CloudProvisionerTemplateForm>({
    defaultValues: {
      file: null,
      fileName: "",
      os: "",
      providerType: "",
      maxCpuCore: 0,
      maxRam: 0,
      maxStorage: 0,
      minCpuCore: 0,
      minRam: 0,
      minStorage: 0,
      flavor: "",
      projectId: "",
      region: "",
    },
  });

  const currentIac = watch("file");

const onIacDrop = useCallback(
  (acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0] ?? null;

    setValue("file", file, { shouldValidate: true });

    if (!file) {
      setError("file", {
        type: "manual",
        message: MESSAGES.MessageUploadIac,
      });
    } else {
      clearErrors("file");
    }
  },
  [setValue, setError, clearErrors]
);


  const submitForm = async (
    data: CloudProvisionerTemplateForm,
    isOvh: boolean,
    limits?: {
      minCpuCore: number;
      maxCpuCore: number;
      minRam: number;
      maxRam: number;
      minStorage: number;
      maxStorage: number;
    }
  ) => {
    //Bulletproof double submit prevention
    if (isSubmittingRef.current) return;

    if (!data.file) {
      setError("file", {
        type: "manual",
        message: MESSAGES.MessageUploadIac,
      });
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    clearErrors("file");

    try {
      let response=null;
      if (isOvh) {
        const parsedData: CloudProvisionerTemplateRequestOvh = {
          ...data,
          os: normalizeFillArray(data.os),
          providerType: "OVH",
        };
        response = await saveCloudProvisionerTemplateOvh(parsedData).unwrap();
        if (!response.success) throw new Error(MESSAGES.MessageErrorSaveCloudProvisionerOvh);
      } else if (limits) {
        const parsedData: CloudProvisionerTemplateRequestIonos = {
          ...data,
          os: normalizeFillArray(data.os),
          providerType: "IONOS",
          ...limits,
        };
        response = await saveCloudProvisionerTemplateIonos(parsedData).unwrap();
        if (!response.success) throw new Error(MESSAGES.MessageErrorSaveCloudProvisionerIonos);
      }

      setTimeout(() => {
        navigate("/cloud-provisioner-template",{state:{success:true,response:response}});
      }, 100);
    } catch (error: any) {
      navigate("/cloud-provisioner-template",{state:{error:error}});
    } finally {
         refetch();
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    control,
    errors,
    submitForm,
    reset,
    setValue,
    currentIac,
    onIacDrop,
    isLoading: isSubmitting || isOvhLoading,
    isError,
    isSubmitting,
  };
};
